import { useEffect, useState } from "react";
import CompletedWorkPhoto from "@/components/ui/CompletedWorkPhoto";
import Reveal from "@/components/ui/Reveal";
import { COMPLETED_WORK_PHOTOS } from "@/data/galleryPhotos";
import api from "@/lib/api";

/**
 * CompletedWorkGrid — responsive grid of A1 Screening's real completed-work photos.
 *
 * Renders the in-bundle static photo set FIRST, then appends any admin-uploaded
 * photos that match the same surface. Static photos are unaffected by the admin
 * photo manager; the new system is purely additive.
 *
 * @param {"home"|"gallery"|"service"} surface
 * @param {number=} limit  Optional cap on total photos rendered.
 * @param {string=} category  Optional category filter for service pages
 *        (e.g. "pool-cage-screen-enclosure"). Static photos are filtered on
 *        their own `category` field; admin photos use the same field server-side.
 * @param {"grid"|"editorial"} [layout="grid"]
 *        - "grid" (default): even 1/2/3-col responsive grid.
 *        - "editorial": first photo featured (spans 2 cols + 2 rows on lg),
 *          remaining photos in compact tiles. Best for the Gallery page.
 */
export default function CompletedWorkGrid({ surface = "gallery", limit, category, layout = "grid" }) {
    const staticFiltered = COMPLETED_WORK_PHOTOS.filter((p) => {
        if (category && p.category !== category) return false;
        if (surface === "home") return p.show_on_homepage;
        if (surface === "service") return p.show_on_service_page;
        return p.show_on_gallery;
    });

    const [adminPhotos, setAdminPhotos] = useState([]);
    useEffect(() => {
        // Map UI surface → backend filter key.
        const surfaceKey = surface === "home"
            ? "homepage"
            : surface === "service"
                ? "service"
                : "gallery";
        const url = `/photos/public?surface=${surfaceKey}${category ? `&category=${category}` : ""}`;
        api.get(url)
            .then((r) => setAdminPhotos(r.data.photos || []))
            .catch(() => setAdminPhotos([]));
    }, [surface, category]);

    // Compose: static first (so existing gallery layout is preserved), admin after.
    // Adapt admin photo shape → the same fields CompletedWorkPhoto expects.
    const adminAdapted = adminPhotos.map((p) => ({
        id: `admin-${p.id}`,
        // Admin photos are a single hosted URL — point base_path at it and let
        // CompletedWorkPhoto's <img> fallback render straight from the URL.
        base_path: null,
        image_url: p.image_url,
        intrinsic: { w: 1200, h: 900 },
        aspect_class: "aspect-[4/3]",
        object_position: "object-center",
        alt: p.alt,
        title: p.title,
        category: p.category,
        is_verified_real_job: true,
        is_before_after: p.is_before_after,
        confirmed_city: p.confirmed_city,
        confirmed_service_type: p.confirmed_service_type,
    }));

    const merged = [...staticFiltered, ...adminAdapted];
    const photos = typeof limit === "number" ? merged.slice(0, limit) : merged;

    if (!photos.length) return null;

    const total = photos.length;

    if (layout === "editorial" && photos.length >= 3) {
        const [first, ...rest] = photos;
        return (
            <div
                className="a1-photo-editorial"
                data-testid={`completed-work-grid-${surface}`}
            >
                <Reveal key={first.id} className="a1-photo-editorial-feature">
                    <CompletedWorkPhoto
                        photo={first}
                        eager={surface === "gallery"}
                        index={0}
                        total={total}
                        featured
                    />
                </Reveal>
                <div className="a1-photo-editorial-rest">
                    {rest.map((photo, restIdx) => (
                        <Reveal key={photo.id} delay={Math.min(restIdx + 1, 5) * 70}>
                            <CompletedWorkPhoto
                                photo={photo}
                                index={restIdx + 1}
                                total={total}
                            />
                        </Reveal>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid={`completed-work-grid-${surface}`}
        >
            {photos.map((photo, idx) => (
                <Reveal key={photo.id} delay={Math.min(idx, 5) * 70}>
                    <CompletedWorkPhoto
                        photo={photo}
                        eager={surface === "gallery" && idx === 0}
                        index={idx}
                        total={total}
                    />
                </Reveal>
            ))}
        </div>
    );
}
